import RBush from "rbush"
import { polygon, bbox, point } from "@turf/turf"
import booleanPointInPolygon from "@turf/boolean-point-in-polygon"
import { IGeofencePolygonWithBBox, IBoundingBox, IGeofenceCoordinates, EGeofenceEventType } from "./interface"

// 🚀 Convert geofence coordinates into polygons with bounding boxes
export const prepareBBox = (geofenceCoordinates: IGeofenceCoordinates[]): IGeofencePolygonWithBBox[] => {
    if (!Array.isArray(geofenceCoordinates)) {
        return []
    }

    return geofenceCoordinates
        .map<IGeofencePolygonWithBBox | null>((geofenceCoordinate, index: number) => {
            if (!Array.isArray(geofenceCoordinate.coordinates)) return null

            try {
                const poly = polygon([geofenceCoordinate.coordinates])
                const box = bbox(poly)

                return {
                    id: `${geofenceCoordinate.id}`,
                    boundingBox: { minX: box[0], minY: box[1], maxX: box[2], maxY: box[3] },
                    polygon: poly,
                    entryNotifications: geofenceCoordinate?.entryNotifications ?? false,
                    exitNotifications: geofenceCoordinate?.exitNotifications ?? false,
                }
            } catch (error) {
                console.error("🚨 Error creating geofence at index", index, ":", error, "Data:", geofenceCoordinate)
                return null
            }
        })
        .filter((geofence): geofence is IGeofencePolygonWithBBox => geofence !== null)
}

const localStorageKey = "GeofenceId"
// 🚀 Get last geofence from localStorage
const getLocalStorageGeofenceId = (): string | null => localStorage.getItem(localStorageKey) ?? null

// 🚀 Save geofence ID to localStorage
const saveLocalStorageGeofenceId = (geofenceId: string) => localStorage.setItem(localStorageKey, geofenceId)

// 🚀 Remove geofence ID from localStorage
const clearLocalStorageGeofenceId = () => localStorage.removeItem(localStorageKey)

export const checkGeofenceEvent = async (
    geofences: IGeofencePolygonWithBBox[],
    gpsPoint: [number, number],
    vehicleId: string | null,
    triggerGeofenceEvent: (
        geofenceId: string,
        eventType: EGeofenceEventType,
        vehicleId: string | null,
        latitude: number,
        longitude: number
    ) => Promise<void>
) => {
    if (!geofences.length || gpsPoint.length !== 2) return

    const [latitude, longitude] = gpsPoint
    const previousGeofenceId = getLocalStorageGeofenceId()

    const tree = new RBush<IBoundingBox>()
    tree.load(geofences.map((geo) => geo.boundingBox))

    // Search for geofences that contain the current GPS point
    const searchResult = tree.search({ minX: longitude, minY: latitude, maxX: longitude, maxY: latitude })

    // Find the first geofence that contains the point
    const currentGeofence = searchResult
        .map((bbox) => geofences.find((g) => g.boundingBox === bbox))
        .find((geofence) => geofence && booleanPointInPolygon(point([longitude, latitude]), geofence.polygon))

    const currentGeofenceId = currentGeofence?.id || null
    // If no geofence was found and no previous geofence exists, do nothing
    if (!currentGeofenceId && !previousGeofenceId) return

    // Entering a geofence
    if (currentGeofenceId && previousGeofenceId !== currentGeofenceId) {
        if (currentGeofence?.entryNotifications) {
            saveLocalStorageGeofenceId(currentGeofenceId)
            await triggerGeofenceEvent(currentGeofenceId, EGeofenceEventType.ENTER, vehicleId, latitude, longitude)
        }
    }
    // Exiting a geofence
    else if (!currentGeofenceId && previousGeofenceId) {
        clearLocalStorageGeofenceId()
        const previousGeofence = geofences.find((geo) => geo.id === previousGeofenceId)
        if (previousGeofence?.exitNotifications) {
            await triggerGeofenceEvent(previousGeofenceId, EGeofenceEventType.EXIT, vehicleId, latitude, longitude)
        }
    }
}
